import React, { useState } from 'react';
import { Camera, Mic, Palette, Smile, Image as ImageIcon } from 'lucide-react';
import VoiceControls from './VoiceControls';
interface JournalEntryProps {
  onSave: (entry: {
    text?: string;
    audio?: string;
    image?: string;
    drawing?: string;
    stickers?: string[];
  }) => void;
}
const JournalEntry = ({
  onSave
}: JournalEntryProps) => {
  const [activeTab, setActiveTab] = useState<'voice' | 'photo' | 'draw' | 'write'>('voice');
  const [isRecording, setIsRecording] = useState(false);
  const inputMethods = [{
    id: 'voice',
    icon: <Mic className="w-8 h-8" data-id="element-88" />,
    label: 'Tell your story',
    description: 'Just talk about your day'
  }, {
    id: 'photo',
    icon: <Camera className="w-8 h-8" data-id="element-89" />,
    label: 'Add a photo',
    description: 'Take a picture or choose from album'
  }, {
    id: 'draw',
    icon: <Palette className="w-8 h-8" data-id="element-90" />,
    label: 'Draw something',
    description: 'Express yourself with colors'
  }, {
    id: 'write',
    icon: <ImageIcon className="w-8 h-8" data-id="element-91" />,
    label: 'Write & Decorate',
    description: 'Add stickers and text'
  }];
  return <div className="space-y-6" data-id="element-92">
      <div className="card" data-id="element-93">
        <h2 className="text-2xl font-semibold mb-4 dark:text-white" data-id="element-94">
          Create a Memory
        </h2>
        <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-95">
          {inputMethods.map(method => <button key={method.id} onClick={() => setActiveTab(method.id as any)} className={`
                p-6 rounded-2xl flex flex-col items-center text-center transition-all
                ${activeTab === method.id ? 'bg-lavender/20 text-lavender ring-2 ring-lavender' : 'bg-gray-50 dark:bg-slate-700 hover:bg-lavender/10'}
              `} aria-label={method.label} data-id="element-96">
              <div className={`mb-3 ${activeTab === method.id ? 'text-lavender' : 'text-gray-500'}`} data-id="element-97">
                {method.icon}
              </div>
              <h3 className="text-lg font-medium mb-1" data-id="element-98">{method.label}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-99">
                {method.description}
              </p>
            </button>)}
        </div>
        <div className="space-y-4" data-id="element-100">
          {activeTab === 'voice' && <div className="text-center" data-id="element-101">
              <VoiceControls onStartRecording={() => setIsRecording(true)} onStopRecording={() => setIsRecording(false)} onTextToSpeech={() => {}} isListening={isRecording} isSpeaking={false} isTextToSpeechEnabled={false} data-id="element-102" />
              <p className="text-gray-600 dark:text-gray-300 mt-4" data-id="element-103">
                {isRecording ? "I'm listening... Take your time to share your thoughts" : "Tap the microphone when you're ready to start"}
              </p>
            </div>}
          {activeTab === 'photo' && <div className="space-y-4" data-id="element-104">
              <button className="w-full p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl hover:border-lavender transition-colors" data-id="element-105">
                <div className="flex flex-col items-center text-gray-500 dark:text-gray-400" data-id="element-106">
                  <Camera className="w-8 h-8 mb-2" data-id="element-107" />
                  <span className="text-lg" data-id="element-108">Take a Photo</span>
                </div>
              </button>
              <button className="w-full p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl hover:border-lavender transition-colors" data-id="element-109">
                <div className="flex flex-col items-center text-gray-500 dark:text-gray-400" data-id="element-110">
                  <ImageIcon className="w-8 h-8 mb-2" data-id="element-111" />
                  <span className="text-lg" data-id="element-112">Choose from Album</span>
                </div>
              </button>
            </div>}
          {activeTab === 'draw' && <div className="text-center p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl" data-id="element-113">
              <div className="flex flex-col items-center text-gray-500 dark:text-gray-400" data-id="element-114">
                <Palette className="w-8 h-8 mb-2" data-id="element-115" />
                <span className="text-lg" data-id="element-116">
                  Simple Drawing Tools Coming Soon
                </span>
                <p className="mt-2 text-sm" data-id="element-117">
                  Express yourself with colors and shapes
                </p>
              </div>
            </div>}
          {activeTab === 'write' && <div className="space-y-4" data-id="element-118">
              <textarea className="w-full p-4 min-h-[150px] rounded-xl border-2 border-gray-200 dark:border-gray-600 
                  focus:ring-2 focus:ring-lavender focus:border-transparent bg-transparent
                  text-xl dark:text-white resize-none" placeholder="Write your thoughts here..." data-id="element-119" />
              <div className="flex flex-wrap gap-2" data-id="element-120">
                <button className="p-3 bg-gray-100 dark:bg-slate-700 rounded-xl hover:bg-lavender/10" data-id="element-121">
                  <Smile className="w-6 h-6 text-gray-500" data-id="element-122" />
                </button>
                {/* Add more sticker/decoration options here */}
              </div>
            </div>}
        </div>
      </div>
      <div className="card bg-mint-green/10 dark:bg-mint-green/5" data-id="element-123">
        <h3 className="text-xl font-medium mb-3 dark:text-white" data-id="element-124">
          Memory Prompts
        </h3>
        <div className="space-y-3" data-id="element-125">
          <button className="w-full p-4 bg-white dark:bg-slate-700 rounded-xl text-left hover:bg-lavender/5" data-id="element-126">
            <p className="text-lg" data-id="element-127">
              Remember the garden flowers you planted last spring?
            </p>
          </button>
          <button className="w-full p-4 bg-white dark:bg-slate-700 rounded-xl text-left hover:bg-lavender/5" data-id="element-128">
            <p className="text-lg" data-id="element-129">How about sharing your favorite recipe?</p>
          </button>
          <button className="w-full p-4 bg-white dark:bg-slate-700 rounded-xl text-left hover:bg-lavender/5" data-id="element-130">
            <p className="text-lg" data-id="element-131">Tell me about a special family moment</p>
          </button>
        </div>
      </div>
    </div>;
};
export default JournalEntry;